import {
    imessageAddressSchema,
    imessagePhoneNumberSchema,
} from "@wags/profile-contracts/imessage-address.contract.ts";
import { z } from "zod";
import { agentActorSchema, jsonValueSchema } from "./common.contract.ts";

const identifierSchema = z.string().min(1).max(512);
const phoneSchema = imessagePhoneNumberSchema;
const timestampSchema = z.iso.datetime({ offset: true });
let reactionEmojiPattern: RegExp | undefined;
const presentationJsonSchema = z.custom<Record<string, unknown>>(
    (value) =>
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        jsonValueSchema.safeParse(value).success,
);

export const userDeliveryAttachmentSchema = z
    .object({
        url: z.url().refine((value) => new URL(value).protocol === "https:"),
        mediaType: z.string().min(1).max(256),
        filename: z.string().min(1).max(512).optional(),
    })
    .strict();

export const sendToUserInputSchema = z.discriminatedUnion("kind", [
    z
        .object({
            kind: z.literal("message"),
            text: z.string().max(131_072).optional(),
            sendStyle: z.literal("confetti").optional(),
            presentation: z
                .object({ toolCallId: identifierSchema })
                .strict()
                .optional(),
            attachments: z
                .array(userDeliveryAttachmentSchema)
                .max(10)
                .default([]),
        })
        .strict()
        .refine(
            (value) =>
                Boolean(value.text?.trim()) ||
                value.attachments.length > 0 ||
                value.presentation !== undefined,
            {
                message:
                    "A message requires text, an attachment, or a presentation",
            },
        ),
    z
        .object({
            kind: z.literal("contact"),
            contact: z.union([
                z.object({ kind: z.literal("self") }).strict(),
                z
                    .object({
                        kind: z.literal("custom"),
                        displayName: z.string().min(1).max(256),
                        phones: z.array(phoneSchema).min(1).max(10),
                        emails: z.array(z.email()).max(10).default([]),
                        organization: z.string().min(1).max(256).optional(),
                    })
                    .strict(),
            ]),
        })
        .strict(),
    z
        .object({
            kind: z.literal("reaction"),
            operation: z.enum(["add", "remove"]),
            messageId: identifierSchema,
            reaction: z.union([
                z.enum([
                    "love",
                    "like",
                    "dislike",
                    "laugh",
                    "emphasize",
                    "question",
                ]),
                z
                    .string()
                    .refine(
                        (value) =>
                            (reactionEmojiPattern ??= new RegExp(
                                "^(?!\\p{Emoji_Modifier}$)\\p{RGI_Emoji}$",
                                "v",
                            )).test(value),
                        "Enter one supported reaction or emoji",
                    ),
            ]),
        })
        .strict(),
]);

export const sessionDeliveryRouteSchema = z.discriminatedUnion("kind", [
    z
        .object({
            kind: z.literal("phone"),
            version: z.literal(1),
            provider: z.enum(["sendblue", "native-imessage"]),
            accountId: identifierSchema,
            senderNumber: phoneSchema,
            recipientNumber: imessageAddressSchema,
            conversation: z.discriminatedUnion("kind", [
                z
                    .object({
                        kind: z.literal("direct"),
                        providerConversationId: identifierSchema,
                    })
                    .strict(),
                z
                    .object({
                        kind: z.literal("group"),
                        providerConversationId: identifierSchema,
                    })
                    .strict(),
            ]),
        })
        .strict(),
    z
        .object({
            kind: z.literal("web"),
            version: z.literal(1),
            conversationId: identifierSchema,
        })
        .strict(),
    z
        .object({
            kind: z.literal("clickup"),
            version: z.literal(1),
            accountId: identifierSchema,
            target: z.discriminatedUnion("kind", [
                z
                    .object({
                        kind: z.literal("chat"),
                        channelId: identifierSchema,
                        parentMessageId: identifierSchema,
                    })
                    .strict(),
                z
                    .object({
                        kind: z.literal("task"),
                        taskId: identifierSchema,
                    })
                    .strict(),
            ]),
        })
        .strict(),
]);

export const userDeliverySourceSchema = z.discriminatedUnion("kind", [
    z
        .object({
            kind: z.literal("tool"),
            sessionId: identifierSchema,
            turnId: identifierSchema,
            toolCallId: identifierSchema,
            attemptGeneration: z.number().int().nonnegative().optional(),
        })
        .strict(),
    z
        .object({
            kind: z.literal("system"),
            sessionId: identifierSchema,
            actionId: identifierSchema,
            actor: agentActorSchema
                .extend({ role: z.literal("operator") })
                .optional(),
        })
        .strict(),
]);

export const userDeliveryStatusSchema = z.enum([
    "queued",
    "awaiting_review",
    "submitting",
    "reconciling",
    "accepted",
    "delivered",
    "rejected",
    "failed",
    "parked",
    "suppressed",
]);

export const userDeliveryPartReceiptSchema = z
    .object({
        partId: identifierSchema,
        status: userDeliveryStatusSchema,
        providerMessageId: z.string().min(1).nullable(),
        error: z.string().max(16_384).nullable(),
        attachmentIndices: z
            .array(z.number().int().nonnegative())
            .max(20)
            .optional(),
    })
    .strict();

export const userDeliveryReceiptSchema = z
    .object({
        deliveryId: identifierSchema,
        sequence: z.number().int().positive(),
        status: userDeliveryStatusSchema,
        parts: z.array(userDeliveryPartReceiptSchema),
        updatedAt: timestampSchema,
    })
    .strict();

export const userDeliveryPublicMessageSchema = z
    .object({
        version: z.literal(1),
        deliveryId: identifierSchema,
        sequence: z.number().int().positive(),
        createdCursor: z.number().int().positive().optional(),
        historicalSourceCursor: z.number().int().positive().optional(),
        createdPartIndex: z.number().int().nonnegative().optional(),
        intent: sendToUserInputSchema,
        requestedIntent: sendToUserInputSchema.optional(),
        presentation: z
            .object({
                kind: z.literal("tool_widget"),
                toolCallId: identifierSchema,
                widget: z.enum([
                    "createQuickReplies",
                    "createProviderContactForm",
                    "createProviderClaimCard",
                    "displayListingCards",
                    "createBreedCards",
                    "connectMcpAccount",
                    "showDogPhotoPicker",
                ]),
                input: presentationJsonSchema,
                output: presentationJsonSchema,
            })
            .strict()
            .optional(),
        receipt: userDeliveryReceiptSchema,
    })
    .strict();

export const USER_DELIVERY_PUBLIC_DATA_NAME = "user_delivery";

export type SendToUserInput = z.infer<typeof sendToUserInputSchema>;
export type SessionDeliveryRoute = z.infer<typeof sessionDeliveryRouteSchema>;
export type UserDeliverySource = z.infer<typeof userDeliverySourceSchema>;
export type UserDeliveryReceipt = z.infer<typeof userDeliveryReceiptSchema>;
export type UserDeliveryStatus = z.infer<typeof userDeliveryStatusSchema>;
export type UserDeliveryAttachment = z.infer<
    typeof userDeliveryAttachmentSchema
>;
export type UserDeliveryPublicMessage = z.infer<
    typeof userDeliveryPublicMessageSchema
>;

export const legacyUserDeliveryHistoryRequestSchema = z
    .object({
        sessionId: identifierSchema,
        cutoffCursor: z.number().int().nonnegative(),
        afterDeliveryId: identifierSchema.optional(),
    })
    .strict();

export const legacyUserDeliveryHistoryPageSchema = z
    .object({
        entries: z
            .array(
                z
                    .object({
                        sourceMessageId: identifierSchema,
                        sourceCursor: z.number().int().positive(),
                        createdAt: timestampSchema,
                        delivery: userDeliveryPublicMessageSchema,
                    })
                    .strict(),
            )
            .max(100),
        nextDeliveryId: identifierSchema.nullable(),
        settled: z.boolean(),
    })
    .strict();

export type LegacyUserDeliveryHistoryRequest = z.infer<
    typeof legacyUserDeliveryHistoryRequestSchema
>;
export type LegacyUserDeliveryHistoryPage = z.infer<
    typeof legacyUserDeliveryHistoryPageSchema
>;
